import { useGetAllPresencesQuery, useGetUserFullPresenceQuery, useInsertGuestUserMutation } from "../../slices/usersApiSlice";
import { useGetAllGroupsQuery } from "../../slices/groupsApiSlice";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dataApplicationsContext from "../../context/dataApplicationsContext";
import { addMonths, addDays, startOfMonth, format, isSameDay, getISODay, endOfMonth, startOfWeek, endOfWeek, eachMonthOfInterval } from "date-fns";
import { fr } from "date-fns/locale";
import { SyncLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faUpRightAndDownLeftFromCenter } from "@fortawesome/free-solid-svg-icons";
import { getWeeksOfMonth, trouverPresencesExceptionnelles } from "./GetWeeksOnMonth";
import Select from 'react-select';


const GeneralVue = () => {
  // ────────────────────────────────────────────────
  // Contexte et données de base
  // ────────────────────────────────────────────────
  const { selectedSeason, selectedGroup, setSelectedGroup } = useContext(dataApplicationsContext);

  const { data: groups, isLoading: isLoadingGroups, isSuccess: isSuccessGroups } = useGetAllGroupsQuery(selectedSeason?.value, { skip: !selectedSeason });
  const { data: presences, isLoading: isLoadingPresences, isSuccess: isSuccessPresences } = useGetAllPresencesQuery(selectedSeason?.value, { skip: !selectedSeason });

  const [insertGuestUser] = useInsertGuestUserMutation();

  // ────────────────────────────────────────────────
  // États locaux
  // ────────────────────────────────────────────────
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [displayUsers, setDisplayUsers] = useState([]);
  const [openAddGuest, setOpenAddGuest] = useState(false);
  const [search, setSearch] = useState('');
  const [vueAnnee, setVueAnnee] = useState(false);
  // Mode "Coach" : option virtuelle du select, gardée en local pour ne pas toucher
  // au selectedGroup du contexte (partagé avec les autres pages)
  const [isCoachView, setIsCoachView] = useState(false);

  // Début et fin de la saison (du 1er septembre au 15 juillet)
  const seasonRange = useMemo(() => {
    if (!selectedSeason?.label) return null;
    const [yearStart, yearEnd] = selectedSeason.label.split('-');
    return {
      debut: new Date(`${yearStart}-09-01`),
      fin: new Date(`${yearEnd}-07-15`),
    };
  }, [selectedSeason]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  }

  const handleReset = () => {
    setSearch('')
  }

  const handleSelect = (option) => {
    if (option?.value === 'coach') {
      setIsCoachView(true);          // on n'écrit PAS 'coach' dans le contexte
    } else {
      setIsCoachView(false);
      setSelectedGroup(option);
    }
  }

  // ────────────────────────────────────────────────
  // Formulaire (react-hook-form)
  // ────────────────────────────────────────────────
  const defaultValues = {
    presence_date: new Date(),
  }

  const schema = yup.object().shape({
  });

  const form = useForm({
    defaultValues, resolver: yupResolver(schema)
  });

  const { watch, handleSubmit } = form;

  // ────────────────────────────────────────────────
  // Période affichée : le mois (semaines complètes) ou toute la saison
  // ────────────────────────────────────────────────
  const bornesAffichage = useMemo(() => {
    if (vueAnnee && seasonRange) {
      return {
        debut: startOfWeek(seasonRange.debut, { weekStartsOn: 1 }),
        fin: endOfWeek(seasonRange.fin, { weekStartsOn: 1 }),
      };
    }
    const debut = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const fin = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return { debut, fin };
  }, [month, vueAnnee, seasonRange]);

  // ────────────────────────────────────────────────
  // Présences complètes du groupe (gymnastes + coachs + planning)
  // ────────────────────────────────────────────────
  const { data: usersFullPresence, isLoading: isLoadingUsersP, isSuccess: isSuccessUsersP } = useGetUserFullPresenceQuery(
    { id_season: selectedSeason?.value,
      date_from: format(bornesAffichage.debut, "yyyy-MM-dd"),
      date_to: format(bornesAffichage.fin, "yyyy-MM-dd"),
      id_group: isCoachView ? 'coach' : selectedGroup?.value
    },
    { skip: !selectedSeason || (!selectedGroup && !isCoachView) }
  );

  // Le back renvoie { users, coaches, planning }
  const usersP = usersFullPresence?.users;
  const coaches = usersFullPresence?.coaches;
  const planning = usersFullPresence?.planning;

  // Coachs filtrés par la recherche texte (le groupe est déjà filtré côté serveur).
  // IMPORTANT : ce useMemo doit se trouver APRÈS la déclaration de usersFullPresence
  // et AVANT le "return" du chargement (les hooks ne peuvent pas être appelés après un return).
  const displayCoaches = useMemo(() => {
    let list = coaches ?? [];
    if (search.length > 0) {
      const normalize = (str) =>
        str?.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const s = normalize(search);
      list = list.filter((c) =>
        normalize(c.last_name)?.includes(s) || normalize(c.first_name)?.includes(s)
      );
    }
    return list;
  }, [coaches, search]);

  // ────────────────────────────────────────────────
  // Liste des gymnastes affichés (filtre groupe + recherche)
  // ────────────────────────────────────────────────
  useEffect(() => {
    if (isSuccessGroups && isSuccessUsersP && isSuccessPresences) {
      let filteredUsers = usersP ?? [];
      if (selectedGroup) {  // 1. Filtre par groupe
        filteredUsers = filteredUsers.filter(user => user.id_group === selectedGroup?.value);
      }
      if (search.length > 0) {  // 2. Filtre par recherche texte
        const normalizeString = (str) =>
          str?.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const normalizedSearch = normalizeString(search);
        const includesSearch = (str) => normalizeString(str)?.includes(normalizedSearch);
        filteredUsers = filteredUsers.filter((user) =>
          includesSearch(user.last_name)
          || includesSearch(user.first_name)
          || includesSearch(user?.birth_date?.split('T')?.[0])
          || includesSearch(user.adresse)
        );
      }
      setDisplayUsers(filteredUsers);
    }
  }, [
    search, selectedGroup, watch('presence_date'), selectedSeason,
    isSuccessPresences, presences, isSuccessGroups, isSuccessUsersP, usersP
  ]);

  const onSubmit = async (data) => {
    const { first_name, last_name, telephone_1, telephone_2, group } = data;
    await insertGuestUser({
      first_name, last_name, birth_date: null, telephone_1, telephone_2, id_group: group.value
    })
    setOpenAddGuest(false)
  }

  // ────────────────────────────────────────────────
  // Semaines affichées
  // ────────────────────────────────────────────────
  const semainesAffichees = useMemo(() => {
    if (vueAnnee && seasonRange) {
      const mois = eachMonthOfInterval({ start: seasonRange.debut, end: seasonRange.fin });
      const semaines = mois.flatMap((m) => getWeeksOfMonth(m));
      // Dédoublonnage : une semaine à cheval sur 2 mois peut être générée deux fois
      const seen = new Set();
      return semaines.filter((s) => {
        if (seen.has(s.label)) return false;
        seen.add(s.label);
        return true;
      });
    }
    return getWeeksOfMonth(month);
  }, [month, vueAnnee, seasonRange]);

  // Pour chaque semaine, calcule la liste des jours d'entraînement réels
  // (en tenant compte des exceptions ponctuelles qui remplacent le planning de base, jour par jour)
  const joursEntrainementParSemaine = useMemo(() => {
    if (!planning) return {};
    const trouverEntrainementDuJour = (date) => {
      const dayOfWeek = getISODay(date);
      const exception = planning.find((p) =>  // Priorité 1 : une exception couvre cette date précise
        p.date_from && p.date_to &&
        p.day_of_week === dayOfWeek &&
        date >= new Date(p.date_from) && date <= new Date(p.date_to)
      );
      if (exception) return { date, training_order: exception.training_order };

      // Priorité 2 : planning de base pour ce jour de semaine (uniquement si aucune exception ne couvre ce jour précis)
      const uneExceptionCouvreCeJour = planning.some((p) =>
        p.date_from && p.date_to &&
        p.day_of_week === dayOfWeek &&
        date >= new Date(p.date_from) && date <= new Date(p.date_to)
      );
      if (uneExceptionCouvreCeJour) return null;
      const base = planning.find((p) => !p.date_from && p.day_of_week === dayOfWeek);
      return base ? { date, training_order: base.training_order } : null;
    };
    const result = {};
    semainesAffichees.forEach((semaine) => {
      const jours = [];
      let curseur = semaine.debut;
      while (curseur <= semaine.fin) {
        const entrainement = trouverEntrainementDuJour(curseur);
        if (entrainement) jours.push(entrainement);
        curseur = addDays(curseur, 1);
      }
      result[semaine.label] = jours.sort((a, b) => a.date - b.date);
    });
    return result;
  }, [planning, semainesAffichees]);

  // Lignes affichées : une seule en vue mois, une par mois en vue année
  const lignesAffichees = useMemo(() => {
    if (!vueAnnee) {
      return [{ label: format(month, "MMMM yyyy", { locale: fr }), semaines: semainesAffichees }];
    }
    const groupes = new Map();
    semainesAffichees.forEach((semaine) => {
      const cleMois = format(semaine.debut, "yyyy-MM");
      if (!groupes.has(cleMois)) {
        groupes.set(cleMois, { label: format(semaine.debut, "MMMM yyyy", { locale: fr }), semaines: [] });
      }
      groupes.get(cleMois).semaines.push(semaine);
    });
    return Array.from(groupes.values());
  }, [semainesAffichees, vueAnnee, month]);

  const nbSemainesMax = useMemo(() => {
    return lignesAffichees.reduce((max, ligne) => Math.max(max, ligne.semaines.length), 0);
  }, [lignesAffichees]);


  // ────────────────────────────────────────────────
  // Écran de chargement (aucun hook après ce return)
  // ────────────────────────────────────────────────
  if (isLoadingGroups || isLoadingUsersP || isLoadingPresences) {
    return (
      <div className="main-loader-container">
        <div className="main-loader">
          <img src="/images/logo_cgv.png" alt="Error-404" className="logo-loader"></img>
          <SyncLoader color='#e1004a' size={16}/>
        </div>
      </div>
    )
  }

  const handleChangeMonth = (value) => {
    setMonth((prevMonth) => startOfMonth(addMonths(prevMonth, value)));
  };

  // Vérifie si la personne était présente (is_present: true) à un jour d'entraînement précis
  const estPresentCeJour = (presencesUser, dateEntrainement) => {
    if (!presencesUser || presencesUser.length === 0) return false;
    return presencesUser.some((presence) =>
      presence.is_present && isSameDay(new Date(presence.presence_date), dateEntrainement)
    );
  };

  // Option virtuelle "Coach" en tête de liste, puis les vrais groupes
  const coachOption = { label: 'Coach', value: 'coach' };
  const optionsGroups = [
    coachOption,
    ...(groups?.map(group => ({ label: group.lib_group, value: group.id_group })) ?? [])
  ];
  const vueAnneeClass = vueAnnee ? 'vue-year' : 'vue-month';

  // ────────────────────────────────────────────────
  // Rendu d'une personne avec ses bâtons de présence.
  // Utilisé pour les gymnastes ET pour les coachs :
  // isCoach ne change que le libellé (pas d'année de naissance) et une classe CSS.
  // ────────────────────────────────────────────────
  const renderUserBloc = (user, key, isCoach = false) => (
    <div className={`user-bloc-container-item ${vueAnneeClass} ${isCoach ? 'coach-bars' : ''}`} key={key}>
      <div className={`line-checkbox-container simpleCheckbox mt-0 flex gap-2`}>
        <p className={`form-label cursor-pointer font-medium`}>
          {user.last_name.toUpperCase()} {user.first_name}
          {/* pas d'année de naissance (ni "essai") pour un coach */}
          {!isCoach && (
            <span className="font-light ml-2">({user?.birth_date ? user.birth_date.split('-')?.[0] : 'essai'})</span>
          )}
        </p>
      </div>
      <div className="semaines-container-wrapper flex flex-col gap-3 mt-1" style={{ '--nb-semaines': nbSemainesMax }}>
        {lignesAffichees.map((ligne) => (
          <div key={ligne.label} className="mois-ligne">
            <div className="semaines-container">
              {ligne.semaines.map((semaine) => {
                const joursEntrainement = joursEntrainementParSemaine[semaine.label] ?? [];
                const presencesExceptionnelles = trouverPresencesExceptionnelles(user.presences, semaine, joursEntrainement);
                return (
                  <div key={semaine.label} className="semaine-colonne flex flex-col items-center">
                    <span className="semaine-label text-xs font-light">{semaine.label}</span>
                    <div className="barres-semaine flex gap-1 mt-1">
                      {/* Bâtons des jours d'entraînement prévus */}
                      {joursEntrainement.map((entrainement, idx) => {
                        const estValidee = estPresentCeJour(user.presences, entrainement.date);
                        const jourPrecedent = joursEntrainement[idx - 1];
                        const changementDeMois = jourPrecedent && entrainement.date.getMonth() !== jourPrecedent.date.getMonth();
                        const horsDuMoisAffiche = !vueAnnee && (entrainement.date.getMonth() !== month.getMonth() || entrainement.date.getFullYear() !== month.getFullYear());
                        return (
                          <Fragment key={idx}>
                            {(changementDeMois && !vueAnnee) && <div className="separateur-mois" />}
                            <div
                              className={`barre-training ${vueAnneeClass} ${estValidee ? 'barre-validee' : 'barre-vide'} ${horsDuMoisAffiche ? 'hors-mois' : ''}`}
                            />
                          </Fragment>
                        );
                      })}
                      {/* Bâtons des présences hors planning */}
                      {presencesExceptionnelles.map((presence, idx) => {
                        const datePresence = new Date(presence.presence_date);
                        const presencePrecedente = presencesExceptionnelles[idx - 1];
                        const changementDeMois = presencePrecedente && datePresence.getMonth() !== new Date(presencePrecedente.presence_date).getMonth();
                        const horsDuMoisAffiche = !vueAnnee && (datePresence.getMonth() !== month.getMonth() || datePresence.getFullYear() !== month.getFullYear());
                        return (
                          <Fragment key={`exception-${idx}`}>
                            {(changementDeMois && !vueAnnee) && <div className="separateur-mois" />}
                            <div
                              className={`barre-training barre-exceptionnelle ${vueAnneeClass} ${horsDuMoisAffiche ? 'hors-mois' : ''}`}
                              title={`Présence exceptionnelle le ${format(datePresence, "dd/MM")}`}
                            />
                          </Fragment>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={`pl-8 pr-8 mt-6`} noValidate>
      {/* Recherche */}
      <div className="search-container w-full mb-4">
        <i className={'bi bi-search input-icon'}></i>
        <input
          type={'text'} className={`search-input`} autoComplete="off" autoCorrect="off"
          placeholder={'Rechercher'} value={search} onChange={handleSearch}
        />
        {search.length > 0 && <i className="bi bi-x-lg close-search" onClick={handleReset}></i>}
      </div>

      {/* Choix du groupe */}
      <Select
        options={optionsGroups} onChange={handleSelect} value={isCoachView ? coachOption : selectedGroup} isClearable={false}
        placeholder="Choisissez une groupe"
        className="input-select-container mb-2" classNamePrefix={"input-select"} isSearchable={true}
      />

      {/* Navigation mois / vue année */}
      <div className="month-container mt-4">
        <div className="month-handler">
          <FontAwesomeIcon icon={faAngleLeft} className="button-prev-next text-2xl" onClick={() => handleChangeMonth(-1)}/>
          <p className="font-semibold text-lg mb-0">{format(month, "MMMM yyyy", { locale: fr })}</p>
          <FontAwesomeIcon icon={faAngleRight} className="button-prev-next text-2xl" onClick={() => handleChangeMonth(1)}/>
        </div>
        <FontAwesomeIcon
          icon={faUpRightAndDownLeftFromCenter}
          className="button-prev-next text-xl"
          onClick={() => setVueAnnee((v) => !v)}
        />
      </div>

      {(!selectedGroup && !isCoachView)
        ? <p className="mt-6 text-lg">Veuillez séléctionner un groupe.</p>
        : isCoachView
        // ────────── Vue "Coach" : tous les coachs assistants, sans gymnastes ──────────
        ? <div className="user-bloc-container mt-12">
            {displayCoaches.length > 0
              ? displayCoaches.map((coach) => renderUserBloc(coach, `coach-${coach.id_user}`, true))
              : <p>Aucun coach assistant pour cette saison.</p>
            }
          </div>
        // ────────── Vue groupe : gymnastes uniquement (comportement d'origine) ──────────
        : <div className="user-bloc-container mt-12">
            {/* Gymnastes */}
            {(displayUsers?.length > 0 && !openAddGuest) &&
              displayUsers.map((user) => {
                const key = user.id_user ? `user-${user.id_user}` : `guest-${user.id_user_guest}`;
                return renderUserBloc(user, key);
              })
            }

            {(displayUsers?.length === 0 && selectedGroup && !openAddGuest) &&
              <p>Oupss... Aucun Gymnaste n'est présent dans ce groupe.</p>
            }
          </div>
      }
    </form>
  )
}

export default GeneralVue